/**
 * Shared Report Components
 *
 * Unified visual components used by both ReportSidebar and ReportPage.
 * Supports optional click handling for navigation in the sidebar context.
 *
 * Design: Editorial document style — each hierarchy level has a distinct
 * visual treatment using typography and spacing, not nested card borders.
 */

import React from 'react'
import { Typography } from 'antd'
import { Building2, Compass, Lightbulb, Film } from 'lucide-react'
import { LabelChip } from '../common/LabelChip'
import type { ProjectLabel } from '@bandos/shared'
import {
  IdeaContainer,
  IdeaTextContent,
  NodeLabelsContainer,
  // Editorial design components
  SectionTypeBadge,
  CompanyBlock,
  CompanyName,
  CompanyFieldLabel,
  CompanyFieldText,
  CompanyChildrenArea,
  NeedBlock,
  NeedStatement,
  IdeasTreeLabel,
  IdeaRow,
  IdeaChildrenArea,
  StandaloneIdeasBlock,
  StandaloneIdeasTitle,
} from '../../Pages/styles/ReportPageStyles'
import { TYPOGRAPHY } from '../../styles/theme'
import { CATEGORY_STYLES } from '../../constants/mindmapConfig'

const { Text } = Typography

// ============================================================================
// TYPES
// ============================================================================

/** Common data shape for venture nodes */
export interface VentureData {
  companyName?: string
  businessModelAndValueProp?: string
  goals?: string
  capabilitiesAndAssets?: string
  productType?: string
  hardConstraints?: string
}

/** Common data shape for unmet-need nodes */
export interface NeedData {
  label?: string
}

/** Common data shape for idea nodes */
export interface IdeaData {
  label?: string
}

/** Optional extra content renderer for ideas (e.g., stakeholder feedback in PDF report) */
export type IdeaExtraRenderer = (nodeId: string) => React.ReactNode

/** Base props for all report section components */
interface BaseReportProps {
  /** Optional click handler - when provided, makes the component interactive */
  onNodeClick?: (e: React.MouseEvent, nodeId: string) => void
  /** Whether this section is currently selected in branch selection mode */
  selected?: boolean
}

// Node type colors are sourced from CATEGORY_STYLES (single source of truth).
// Use CATEGORY_STYLES[category].iconColor — do not redefine locally.

// ============================================================================
// VENTURE COMPONENT
// ============================================================================

interface VentureSectionProps extends BaseReportProps {
  nodeId: string
  data: VentureData
  /** Extra content renderer for this node's own surveys/storyboards */
  renderNodeExtra?: IdeaExtraRenderer
  /** Nested content (unmet needs) to render inside the venture section */
  children?: React.ReactNode
}

export function VentureSection({
  nodeId,
  data,
  onNodeClick,
  selected,
  renderNodeExtra,
  children,
}: VentureSectionProps) {
  const handleClick = (e: React.MouseEvent) => {
    if (onNodeClick) {
      e.stopPropagation()
      onNodeClick(e, nodeId)
    }
  }

  return (
    <>
      <CompanyBlock
        $clickable={!!onNodeClick}
        $selected={selected}
        onClick={onNodeClick ? handleClick : undefined}
      >
        <SectionTypeBadge $color={CATEGORY_STYLES.venture.iconColor}>
          <Building2 size={10} />
          Venture
        </SectionTypeBadge>

        <CompanyName>{data.companyName ?? 'Unnamed Venture'}</CompanyName>

        {data.businessModelAndValueProp && (
          <>
            <CompanyFieldLabel>What are you building?</CompanyFieldLabel>
            <CompanyFieldText>{data.businessModelAndValueProp}</CompanyFieldText>
          </>
        )}

        {data.goals && (
          <>
            <CompanyFieldLabel>Who is it for?</CompanyFieldLabel>
            <CompanyFieldText>{data.goals}</CompanyFieldText>
          </>
        )}

        {data.capabilitiesAndAssets && (
          <>
            <CompanyFieldLabel>Available Strengths</CompanyFieldLabel>
            <CompanyFieldText>{data.capabilitiesAndAssets}</CompanyFieldText>
          </>
        )}

        {data.productType && (
          <>
            <CompanyFieldLabel>Format</CompanyFieldLabel>
            <CompanyFieldText>{data.productType}</CompanyFieldText>
          </>
        )}

        {data.hardConstraints && (
          <>
            <CompanyFieldLabel>Project Boundaries</CompanyFieldLabel>
            <CompanyFieldText>{data.hardConstraints}</CompanyFieldText>
          </>
        )}

        {renderNodeExtra?.(nodeId)}
      </CompanyBlock>

      {children && <CompanyChildrenArea>{children}</CompanyChildrenArea>}
    </>
  )
}

// ============================================================================
// UNMET NEED COMPONENT
// ============================================================================

interface NeedSectionProps<TNode> extends BaseReportProps {
  nodeId: string
  data: NeedData
  /** Function to get child idea nodes for this need */
  getChildIdeas: (parentId: string) => TNode[]
  /** Function to extract idea data from a node */
  getIdeaData: (node: TNode) => IdeaData
  /** Function to get node ID */
  getNodeId: (node: TNode) => string
  /** Get labels for a node */
  getNodeLabels?: (nodeId: string) => ProjectLabel[]
  /** Extra content renderer for this need node's own surveys/storyboards */
  renderNodeExtra?: IdeaExtraRenderer
  /** Optional renderer for extra content after each idea (e.g., stakeholder feedback) */
  renderIdeaExtra?: IdeaExtraRenderer
  /** When false, idea rows are hidden but renderExtra (storyboards/surveys) still renders */
  isIdeaVisible?: boolean
  /** Node IDs currently selected in branch selection mode (passed to child ideas) */
  selectedNodeIds?: Set<string>
}

export function NeedSection<TNode>({
  nodeId,
  data,
  getChildIdeas,
  getIdeaData,
  getNodeId,
  onNodeClick,
  selected,
  getNodeLabels,
  renderNodeExtra,
  renderIdeaExtra,
  isIdeaVisible = true,
  selectedNodeIds,
}: NeedSectionProps<TNode>) {
  const handleClick = (e: React.MouseEvent) => {
    if (onNodeClick) {
      e.stopPropagation()
      onNodeClick(e, nodeId)
    }
  }

  const childIdeas = getChildIdeas(nodeId)

  return (
    <NeedBlock
      $clickable={!!onNodeClick}
      $selected={selected}
      onClick={onNodeClick ? handleClick : undefined}
    >
      <SectionTypeBadge $color={CATEGORY_STYLES.need.iconColor}>
        <Compass size={10} />
        Unmet need
      </SectionTypeBadge>

      <NeedStatement>{data.label}</NeedStatement>

      {renderNodeExtra?.(nodeId)}

      {childIdeas.length > 0 && (
        <>
          {isIdeaVisible && (
            <IdeasTreeLabel>
              <Lightbulb size={10} />
              Ideas
            </IdeasTreeLabel>
          )}
          <IdeaChildrenArea>
            {childIdeas.map((idea) => (
              <IdeaItem
                key={getNodeId(idea)}
                nodeId={getNodeId(idea)}
                data={getIdeaData(idea)}
                getChildIdeas={getChildIdeas}
                getIdeaData={getIdeaData}
                getNodeId={getNodeId}
                onNodeClick={onNodeClick}
                getNodeLabels={getNodeLabels}
                renderExtra={renderIdeaExtra}
                isIdeaVisible={isIdeaVisible}
                selectedNodeIds={selectedNodeIds}
              />
            ))}
          </IdeaChildrenArea>
        </>
      )}
    </NeedBlock>
  )
}

// ============================================================================
// IDEA COMPONENT (RECURSIVE)
// ============================================================================

interface IdeaItemProps<TNode> extends BaseReportProps {
  nodeId: string
  data: IdeaData
  /** Function to get child idea nodes */
  getChildIdeas: (parentId: string) => TNode[]
  /** Function to extract idea data from a node */
  getIdeaData: (node: TNode) => IdeaData
  /** Function to get node ID */
  getNodeId: (node: TNode) => string
  /** Get labels for a node */
  getNodeLabels?: (nodeId: string) => ProjectLabel[]
  /** Visited IDs for cycle detection */
  visitedIds?: Set<string>
  /** Optional renderer for extra content after idea (e.g., stakeholder feedback) */
  renderExtra?: IdeaExtraRenderer
  /** When false, idea row is hidden but renderExtra (storyboards/surveys) still renders recursively */
  isIdeaVisible?: boolean
  /** Node IDs currently selected in branch selection mode */
  selectedNodeIds?: Set<string>
}

function IdeaItem<TNode>({
  nodeId,
  data,
  getChildIdeas,
  getIdeaData,
  getNodeId,
  onNodeClick,
  getNodeLabels,
  visitedIds = new Set(),
  renderExtra,
  isIdeaVisible = true,
  selectedNodeIds,
}: IdeaItemProps<TNode>) {
  // Cycle detection
  if (visitedIds.has(nodeId)) {
    return null
  }
  const newVisited = new Set(visitedIds).add(nodeId)
  // First call has an empty visitedIds set — first-level items have no connectors or indent
  const isNested = visitedIds.size > 0

  // When ideas are hidden: skip the row but still render extras and recurse into sub-ideas
  if (!isIdeaVisible) {
    const childIdeas = getChildIdeas(nodeId)
    return (
      <>
        {renderExtra?.(nodeId)}
        {childIdeas.map((childIdea) => (
          <IdeaItem
            key={getNodeId(childIdea)}
            nodeId={getNodeId(childIdea)}
            data={getIdeaData(childIdea)}
            getChildIdeas={getChildIdeas}
            getIdeaData={getIdeaData}
            getNodeId={getNodeId}
            onNodeClick={onNodeClick}
            getNodeLabels={getNodeLabels}
            visitedIds={newVisited}
            renderExtra={renderExtra}
            isIdeaVisible={false}
            selectedNodeIds={selectedNodeIds}
          />
        ))}
      </>
    )
  }

  const handleClick = (e: React.MouseEvent) => {
    if (onNodeClick) {
      e.stopPropagation()
      onNodeClick(e, nodeId)
    }
  }

  const labels = getNodeLabels?.(nodeId) ?? []
  const childIdeas = getChildIdeas(nodeId)
  const hasChildren = childIdeas.length > 0

  return (
    <IdeaContainer $nested={isNested}>
      <IdeaRow
        $clickable={!!onNodeClick}
        $selected={selectedNodeIds?.has(nodeId)}
        onClick={onNodeClick ? handleClick : undefined}
      >
        <IdeaTextContent>
          <Text style={{ fontSize: TYPOGRAPHY.caption.fontSize, lineHeight: 1.5 }}>
            {data.label}
          </Text>
          {labels.length > 0 && (
            <NodeLabelsContainer>
              {labels.map((label) => (
                <LabelChip key={label.id} text={label.label_text} color={label.color} />
              ))}
            </NodeLabelsContainer>
          )}
        </IdeaTextContent>
      </IdeaRow>

      {/* Optional extra content (e.g., stakeholder feedback) */}
      {renderExtra?.(nodeId)}

      {hasChildren && (
        <IdeaChildrenArea $nested>
          {childIdeas.map((childIdea) => (
            <IdeaItem
              key={getNodeId(childIdea)}
              nodeId={getNodeId(childIdea)}
              data={getIdeaData(childIdea)}
              getChildIdeas={getChildIdeas}
              getIdeaData={getIdeaData}
              getNodeId={getNodeId}
              onNodeClick={onNodeClick}
              getNodeLabels={getNodeLabels}
              visitedIds={newVisited}
              renderExtra={renderExtra}
              selectedNodeIds={selectedNodeIds}
            />
          ))}
        </IdeaChildrenArea>
      )}
    </IdeaContainer>
  )
}

// ============================================================================
// STANDALONE IDEAS SECTION
// ============================================================================

interface StandaloneIdeasSectionProps<TNode> extends BaseReportProps {
  /** Root standalone ideas to render */
  ideas: TNode[]
  /** Function to get child idea nodes */
  getChildIdeas: (parentId: string) => TNode[]
  /** Function to extract idea data from a node */
  getIdeaData: (node: TNode) => IdeaData
  /** Function to get node ID */
  getNodeId: (node: TNode) => string
  /** Get labels for a node */
  getNodeLabels?: (nodeId: string) => ProjectLabel[]
  /** Optional renderer for extra content after each idea (e.g., stakeholder feedback) */
  renderIdeaExtra?: IdeaExtraRenderer
  /** When false, idea rows are hidden but renderExtra (storyboards/surveys) still renders */
  isIdeaVisible?: boolean
  /** Node IDs currently selected in branch selection mode */
  selectedNodeIds?: Set<string>
}

export function StandaloneIdeasSection<TNode>({
  ideas,
  getChildIdeas,
  getIdeaData,
  getNodeId,
  onNodeClick,
  getNodeLabels,
  renderIdeaExtra,
  isIdeaVisible = true,
  selectedNodeIds,
}: StandaloneIdeasSectionProps<TNode>) {
  if (ideas.length === 0) return null

  return (
    <StandaloneIdeasBlock>
      {isIdeaVisible && (
        <StandaloneIdeasTitle>
          <Lightbulb size={14} color="#f59e0b" />
          Ideas
        </StandaloneIdeasTitle>
      )}
      <IdeaChildrenArea>
        {ideas.map((idea) => (
          <IdeaItem
            key={getNodeId(idea)}
            nodeId={getNodeId(idea)}
            data={getIdeaData(idea)}
            getChildIdeas={getChildIdeas}
            getIdeaData={getIdeaData}
            getNodeId={getNodeId}
            onNodeClick={onNodeClick}
            getNodeLabels={getNodeLabels}
            renderExtra={renderIdeaExtra}
            isIdeaVisible={isIdeaVisible}
            selectedNodeIds={selectedNodeIds}
          />
        ))}
      </IdeaChildrenArea>
    </StandaloneIdeasBlock>
  )
}

// ============================================================================
// STORYBOARD SECTION
// ============================================================================

const STORYBOARD_FRAME_TITLES = ['The Situation', 'The Struggle', 'The Solution', 'The Outcome']

export interface StoryboardData {
  id: string
  storyboardTitle?: string
  storyboardUrls: string[]
  frameCaptions?: string[]
  isGenerating?: boolean
}

interface StoryboardsSectionProps {
  storyboards: StoryboardData[]
  /** Optional renderer for extra content (e.g., surveys) attached to each storyboard node */
  renderExtra?: (nodeId: string) => React.ReactNode
  onNodeClick?: (e: React.MouseEvent, nodeId: string) => void
}

export function StoryboardsSection({
  storyboards,
  renderExtra,
  onNodeClick,
}: StoryboardsSectionProps) {
  const visible = storyboards.filter((s) => !s.isGenerating && s.storyboardUrls.length === 4)
  if (visible.length === 0) return null

  return (
    <div style={{ marginTop: 12 }}>
      {visible.map((sb) => (
        <div
          key={sb.id}
          style={{
            marginBottom: 12,
            padding: '10px 12px',
            background: 'var(--color-bg-secondary, #fafafa)',
            borderRadius: 6,
            border: '1px solid var(--color-border, #e8e8e8)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              marginBottom: 8,
              cursor: onNodeClick ? 'pointer' : 'default',
            }}
            onClick={
              onNodeClick
                ? (e) => {
                    e.stopPropagation()
                    onNodeClick(e, sb.id)
                  }
                : undefined
            }
          >
            <Film size={14} color="#8b5cf6" />
            <Text strong style={{ color: '#8b5cf6' }}>
              {sb.storyboardTitle ?? 'Storyboard'}
            </Text>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 8,
            }}
          >
            {[0, 1, 2, 3].map((i) => (
              <div key={i} style={{ minWidth: 0 }}>
                <img
                  src={sb.storyboardUrls[i]}
                  alt={STORYBOARD_FRAME_TITLES[i]}
                  style={{
                    width: '100%',
                    aspectRatio: '4/3',
                    objectFit: 'cover',
                    borderRadius: 4,
                    display: 'block',
                  }}
                />
                <Text strong style={{ display: 'block', marginTop: 4 }}>
                  {STORYBOARD_FRAME_TITLES[i]}
                </Text>
                {sb.frameCaptions?.[i] && (
                  <Text style={{ fontStyle: 'italic', lineHeight: 1.4, display: 'block' }}>
                    {sb.frameCaptions[i]}
                  </Text>
                )}
              </div>
            ))}
          </div>
          {renderExtra?.(sb.id)}
        </div>
      ))}
    </div>
  )
}
